import { CONTACT_HINT, EXPERIENCE, HOME, LINKS, PROJECTS } from "../data/shell";
import { IMAGE, PROJECT, PROJECT_CWD } from "./project";

// A small shell for the terminals (zsh in Ghostty, bash in Zed): the home folder holds the resume
// as files (data/shell.js) and, once it has loaded, this site's own project, the usual commands
// work on them, and a few friendly ones (help, about, experience...) give the short versions.
//
// Output is a list of lines. A line is a string, or a list of pieces: strings and
// { text, tone, href }, where `tone` is "bold", "dim" or "dir".

export const USER = "sylvester";
const HOME_PATH = `/Users/${USER}`;

// The working folder is a list of folder names under the home folder ([] is ~).
export const cwdLabel = (cwd) => ["~", ...cwd].join("/");

// Just the folder's own name, as the prompt shows it.
export const folderLabel = (cwd) => (cwd.length ? cwd[cwd.length - 1] : "~");

// Whether the folder is in the project (a Git repository, so the prompt shows the branch).
export const inProject = (cwd) => PROJECT_CWD.every((name, i) => cwd[i] === name);

// What differs between the two shells: their variables, and how they word errors.
const SHELLS = {
  zsh: {
    SHELL: "/bin/zsh",
    TERM: "xterm-ghostty",
    notFound: (name) => `zsh: command not found: ${name}`,
    noSuchDir: (path) => `cd: no such file or directory: ${path}`,
    notDir: (path) => `cd: not a directory: ${path}`,
  },
  bash: {
    SHELL: "/opt/homebrew/bin/bash",
    TERM: "xterm-256color",
    notFound: (name) => `bash: ${name}: command not found`,
    noSuchDir: (path) => `bash: cd: ${path}: No such file or directory`,
    notDir: (path) => `bash: cd: ${path}: Not a directory`,
  },
};

// The project as shell files: folders, and files that know their path in it.
const mount = (node, path) =>
  node.dir
    ? {
        items: Object.fromEntries(
          node.children.map((child) => [child.name, mount(child, path ? `${path}/${child.name}` : child.name)]),
        ),
      }
    : { path, binary: Boolean(node.binary) };

// The home folder, with the project in it once its tree has loaded.
export const homeWith = (tree) => {
  if (!tree) return HOME;
  const [desktop] = PROJECT_CWD;
  return { items: { ...HOME.items, [desktop]: { items: { [PROJECT]: mount(tree, "") } } } };
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (n) => String(n).padStart(2, "0");

// As login(1) and date(1) write it: "Sun Oct 25 23:29:48".
export const formatLogin = (d) =>
  `${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2)} ${two(d.getHours())}:${two(
    d.getMinutes(),
  )}:${two(d.getSeconds())}`;

const timeZone = (d) => {
  const part = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
    .formatToParts(d)
    .find((p) => p.type === "timeZoneName");
  return part ? part.value : "UTC";
};

// Where a path points, as a list of folder names under home; null if it leaves home.
const resolve = (cwd, path) => {
  let rest = path;
  let parts = [...cwd];
  if (rest === "~" || rest.startsWith("~/")) {
    parts = [];
    rest = rest.slice(1);
  } else if (rest === HOME_PATH || rest.startsWith(`${HOME_PATH}/`)) {
    parts = [];
    rest = rest.slice(HOME_PATH.length);
  } else if (rest.startsWith("/")) return null;
  rest.split("/").forEach((name) => {
    if (name === "..") parts.pop();
    else if (name && name !== ".") parts.push(name);
  });
  return parts;
};

const lookup = (home, parts) =>
  parts && parts.reduce((node, name) => (node && node.items ? node.items[name] : undefined), home);

const isDir = (node) => Boolean(node && node.items);

// A folder's contents in columns on 8-character tab stops, as many as fit the terminal's
// `columns`, filled top to bottom as ls lays them out (without the hidden ones, unless `all`).
const listing = (dir, all, columns) => {
  const names = Object.keys(dir.items)
    .filter((n) => all || !n.startsWith("."))
    .sort();
  if (!names.length) return [];
  const width = Math.ceil((Math.max(...names.map((n) => n.length)) + 1) / 8) * 8;
  const across = Math.max(1, Math.floor(columns / width));
  const rows = Math.ceil(names.length / across);
  return Array.from({ length: rows }, (_, row) => {
    const line = names.filter((n, i) => i % rows === row);
    return line.map((n, i) => {
      const text = i < line.length - 1 ? n.padEnd(width) : n;
      return isDir(dir.items[n]) ? { text, tone: "dir" } : text;
    });
  });
};

const HELP = [
  "Here's what you can run:",
  "",
  ...[
    ["about", "who I am"],
    ["experience", "where I've worked"],
    ["projects", "things I've built"],
    ["skills", "languages and tools I use"],
    ["contact", "how to reach me"],
    ["resume", "open my resume in Notes"],
  ].map(([name, what]) => ["  ", { text: name.padEnd(13), tone: "bold" }, what]),
  "",
  "ls, cd, cat, open, pwd, whoami, echo, date, history, clear and exit work too.",
  [{ text: "Tab completes, ↑ and ↓ step through what you've run.", tone: "dim" }],
];

const ok = (lines = []) => ({ lines, status: 0 });
const fail = (lines, status = 1) => ({ lines, status });

// Past this many lines, cat stops and points at the editor.
const MAX_CAT = 2000;

// A project file's lines, once fetched. Images and other binary files aren't printed.
const projectLines = (node, path) => {
  const name = path.split("/").pop();
  if (IMAGE.test(name)) return [[{ text: `${name} is an image. Run open ${path} to see it in Zed.`, tone: "dim" }]];
  if (node.binary) return [[{ text: `${name} is a binary file, so it isn't printed.`, tone: "dim" }]];
  return node.read(node.path).then(
    (text) => {
      const lines = text.replace(/\n$/, "").split("\n");
      if (lines.length <= MAX_CAT) return lines;
      const more = `… ${lines.length - MAX_CAT} more lines. Run open ${path} to read it all in Zed.`;
      return [...lines.slice(0, MAX_CAT), [{ text: more, tone: "dim" }]];
    },
    () => [`cat: ${path}: Input/output error`],
  );
};

// Prints files. A project file has to be fetched first: then the result comes as `pending`, which
// gives the lines when they've all arrived.
const cat = (args, { cwd, home, actions }) => {
  const parts = [];
  let status = 0;
  args.forEach((path) => {
    const node = lookup(home, resolve(cwd, path));
    if (!node) {
      parts.push([`cat: ${path}: No such file or directory`]);
      status = 1;
    } else if (isDir(node)) {
      parts.push([`cat: ${path}: Is a directory`]);
      status = 1;
    } else if (node.path !== undefined) parts.push(projectLines({ ...node, read: actions.readFile }, path));
    else parts.push(node.lines);
  });
  if (!parts.some((part) => part.then)) return { lines: parts.flat(), status };
  return { lines: [], status, pending: Promise.all(parts).then((all) => ({ lines: all.flat(), status })) };
};

const COMMANDS = {
  help: () => ok(HELP),
  about: (args, ctx) => cat(["~/about.txt"], ctx),
  experience: () => ok(EXPERIENCE),
  projects: () => ok(PROJECTS),
  skills: (args, ctx) => cat(["~/skills.txt"], ctx),
  contact: (args, ctx) => ok([...cat(["~/contact.txt"], ctx).lines, "", CONTACT_HINT]),
  resume: (args, { actions }) => {
    actions.openResume(null);
    return ok();
  },

  ls: (args, { cwd, home, columns }) => {
    const paths = args.filter((a) => !a.startsWith("-"));
    const all = args.some((a) => /^-\w*[aA]/.test(a));
    const targets = paths.length ? paths : ["."];
    const lines = [];
    let status = 0;
    targets.forEach((path) => {
      const node = lookup(home, resolve(cwd, path));
      if (!node) {
        lines.push(`ls: ${path}: No such file or directory`);
        status = 1;
        return;
      }
      if (targets.length > 1) lines.push(...(lines.length ? [""] : []), `${path}:`);
      lines.push(...(isDir(node) ? listing(node, all, columns) : [path]));
    });
    return { lines, status };
  },
  cd: (args, { cwd, home, shell }) => {
    const path = args[0] || "~";
    const parts = resolve(cwd, path);
    const node = lookup(home, parts);
    if (!node) return fail([SHELLS[shell].noSuchDir(path)]);
    if (!isDir(node)) return fail([SHELLS[shell].notDir(path)]);
    return { lines: [], status: 0, cwd: parts };
  },
  cat: (args, ctx) => cat(args, ctx),
  open: (args, { cwd, home, actions }) => {
    if (args[0] === "-a") {
      const app = args.slice(1).join(" ");
      return actions.openApp(app) ? ok() : fail([`Unable to find application named '${app}'`]);
    }
    if (!args.length) return fail(["Usage: open [-a <application>] [filenames | URL]"]);
    const lines = [];
    let status = 0;
    args.forEach((target) => {
      const link = LINKS[target.toLowerCase()];
      const node = lookup(home, resolve(cwd, target));
      if (/^(https?:\/\/|mailto:)/i.test(target)) actions.openUrl(target);
      else if (link) actions.openUrl(link);
      else if (isDir(node)) actions.openApp("Finder");
      else if (node && node.path !== undefined) actions.openFile(node.path);
      else if (node) actions.openResume(node.anchor);
      else {
        const parts = resolve(cwd, target);
        lines.push(`The file ${parts ? [HOME_PATH, ...parts].join("/") : target} does not exist.`);
        status = 1;
      }
    });
    return { lines, status };
  },
  pwd: (args, { cwd }) => ok([[HOME_PATH, ...cwd].join("/")]),
  whoami: () => ok([USER]),
  echo: (args) => ok([args.join(" ")]),
  date: () => {
    const now = new Date();
    return ok([`${formatLogin(now)} ${timeZone(now)} ${now.getFullYear()}`]);
  },
  history: (args, { history }) => ok(history.map((h, i) => `${String(i + 1).padStart(5)}  ${h}`)),
  clear: () => ({ lines: [], status: 0, clear: true }),
  exit: () => ({ lines: [], status: 0, exit: true }),
  sudo: () => fail([`${USER} is not in the sudoers file. This incident will be reported.`]),
};

// Words, keeping quoted ones together, after putting in the shell variables.
const words = (line, ctx) => {
  const vars = {
    USER,
    HOME: HOME_PATH,
    SHELL: SHELLS[ctx.shell].SHELL,
    TERM: SHELLS[ctx.shell].TERM,
    PWD: [HOME_PATH, ...ctx.cwd].join("/"),
    "?": String(ctx.status),
  };
  const expanded = line.replace(/\$(\w+|\?)/g, (m, name) => (name in vars ? vars[name] : ""));
  return (expanded.match(/"[^"]*"|'[^']*'|\S+/g) || []).map((w) => w.replace(/^(["'])(.*)\1$/, "$2"));
};

// Runs a command line. `ctx` is { shell ("zsh" or "bash"), home (homeWith), columns (the
// terminal's width), cwd, status, history, hinted, actions }, where `actions` are { openResume(anchor), openApp(name) -> found?,
// openUrl(href), openFile(path), readFile(path) -> promise of the text }. Returns { lines, status,
// and maybe cwd, clear, exit, hinted, and pending: a promise of { lines, status } still to come }.
export const run = (line, given) => {
  const ctx = { shell: "zsh", home: HOME, columns: 80, ...given };
  const [name, ...args] = words(line, ctx);
  const command = Object.prototype.hasOwnProperty.call(COMMANDS, name) && COMMANDS[name];
  if (command) return command(args, ctx);
  const lines = [SHELLS[ctx.shell].notFound(name)];
  // The first miss says where to start.
  if (!ctx.hinted) lines.push([{ text: "Type help to see what you can run here.", tone: "dim" }]);
  return { lines, status: 127, hinted: true };
};

const commonPrefix = (list) =>
  list.reduce((prefix, s) => {
    let i = 0;
    while (i < prefix.length && prefix[i] === s[i]) i += 1;
    return prefix.slice(0, i);
  });

// Tab: completes the word before the cursor, a command first and then file names (only folders
// after cd). Returns { input, options }: the completed line, or the choices when it can't decide.
export const complete = (input, cwd, home = HOME) => {
  const [, before, word] = input.match(/^(.*?)(\S*)$/);
  let choices;
  if (!before.trim()) {
    choices = Object.keys(COMMANDS)
      .sort()
      .filter((c) => c.startsWith(word))
      .map((c) => ({ label: c, text: `${c} ` }));
  } else {
    const slash = word.lastIndexOf("/");
    const folder = word.slice(0, slash + 1);
    const dir = lookup(home, resolve(cwd, folder || "."));
    if (!isDir(dir)) return { input, options: [] };
    const foldersOnly = before.trim() === "cd";
    choices = Object.keys(dir.items)
      .sort()
      .filter((n) => n.startsWith(word.slice(slash + 1)) && (!foldersOnly || isDir(dir.items[n])))
      .filter((n) => !n.startsWith(".") || word.slice(slash + 1).startsWith("."))
      .map((n) =>
        isDir(dir.items[n])
          ? { label: { text: `${n}/`, tone: "dir" }, text: `${folder}${n}/` }
          : { label: n, text: `${folder}${n} ` },
      );
  }
  if (choices.length === 1) return { input: before + choices[0].text, options: [] };
  if (!choices.length) return { input, options: [] };
  const prefix = commonPrefix(choices.map((c) => c.text));
  if (prefix.length > word.length) return { input: before + prefix, options: [] };
  return { input, options: choices.map((c) => c.label) };
};

// Commands in the order they're suggested as you type.
const SUGGESTED = ["help", "about", "experience", "projects", "skills", "contact", "resume", ...Object.keys(COMMANDS)];

// The rest of a line, suggested in gray as you type (like zsh-autosuggestions): the latest
// matching line you've run, or else a command.
export const suggest = (input, history) => {
  if (!input) return "";
  const earlier = [...history].reverse().find((h) => h.startsWith(input) && h !== input);
  if (earlier) return earlier.slice(input.length);
  if (/\s/.test(input)) return "";
  const command = SUGGESTED.find((c) => c.startsWith(input) && c !== input);
  return command ? command.slice(input.length) : "";
};
